import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement } from '~/utils/rbac'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Chaque onglet a sa route dédiée sous `/hr/planning/*` (dashboard, calendar, …), un tab = une route. */
export type HrTab = 'dashboard' | 'calendar' | 'collaborators' | 'attendance' | 'absence-calendar'

export const PLANNING_READ: PermissionTuple = ['read', 'hr.planning.weekly-planning']
export const PLANNING_ADD: PermissionTuple = ['add', 'hr.planning.weekly-planning']
export const PLANNING_EDIT: PermissionTuple = ['edit', 'hr.planning.weekly-planning']
export const PLANNING_DELETE: PermissionTuple = ['delete', 'hr.planning.weekly-planning']
export const PLANNING_EXPORT: PermissionTuple = ['export', 'hr.planning.weekly-planning']

export const STAFF_READ: PermissionTuple = ['read', 'hr.staff.collaborator']
export const STAFF_ADD: PermissionTuple = ['add', 'hr.staff.collaborator']
export const STAFF_EDIT: PermissionTuple = ['edit', 'hr.staff.collaborator']
export const STAFF_DELETE: PermissionTuple = ['delete', 'hr.staff.collaborator']
export const STAFF_EXPORT: PermissionTuple = ['export', 'hr.staff.collaborator']
export const STAFF_IMPORT: PermissionTuple = ['import', 'hr.staff.collaborator']

export const CLOCKING_READ: PermissionTuple = ['read', 'hr.clocking.clocking']
export const CLOCKING_ADD: PermissionTuple = ['add', 'hr.clocking.clocking']
export const CLOCKING_EDIT: PermissionTuple = ['edit', 'hr.clocking.clocking']
export const CLOCKING_EXPORT: PermissionTuple = ['export', 'hr.clocking.clocking']

export const ABSENCE_READ: PermissionTuple = ['read', 'hr.absences.absence']
export const ABSENCE_ADD: PermissionTuple = ['add', 'hr.absences.absence']
export const ABSENCE_DELETE: PermissionTuple = ['delete', 'hr.absences.absence']
export const ABSENCE_EXPORT: PermissionTuple = ['export', 'hr.absences.absence']

/**
 * Valider (ou dévalider) les heures d'un créneau passé : action spéciale ET action de base, à
 * l'identique de la garde posée sur `POST /hr/work-shifts/:id/validate`.
 */
export const CLOCKING_VALIDATE: PermissionRequirement = [
  CLOCKING_EDIT,
  ['validate-clocking', 'hr.clocking.clocking'],
]

/**
 * Les absences d'un collaborateur se saisissent depuis le calendrier des absences comme depuis sa
 * fiche : l'un OU l'autre droit suffit, comme côté backend sur
 * `/hr/collaborators/:id/unavailabilities`.
 */
export const UNAVAILABILITY_ADD: PermissionRequirement = { any: [ABSENCE_ADD, STAFF_ADD] }
export const UNAVAILABILITY_DELETE: PermissionRequirement = { any: [ABSENCE_DELETE, STAFF_DELETE] }

/**
 * Onglet « Tableau de bord » (flag `dashboard-rh`) : la taxonomie n'expose pas de ressource propre
 * à ce sous-onglet. Il agrège l'effectif, la couverture des plannings et les heures pointées, d'où
 * le OU sur ces trois lectures.
 */
export const HR_DASHBOARD_READ: PermissionRequirement = {
  any: [PLANNING_READ, STAFF_READ, CLOCKING_READ],
}

/** Accès à l'écran : dès qu'un seul des sous-onglets est accessible. */
export const HR_PAGE_READ: PermissionRequirement = {
  any: [PLANNING_READ, STAFF_READ, CLOCKING_READ, ABSENCE_READ],
}

/**
 * Source unique des droits de lecture des sous-onglets RH.
 *
 * Consommée par la barre d'onglets, par le `staticData.permission` des routes et par la
 * redirection de `/hr/planning` : un seul endroit à mettre à jour, pas de dérive possible entre
 * l'onglet affiché et son contenu.
 */
export const HR_TAB_PERMISSIONS = {
  dashboard: HR_DASHBOARD_READ,
  calendar: PLANNING_READ,
  collaborators: STAFF_READ,
  attendance: CLOCKING_READ,
  'absence-calendar': ABSENCE_READ,
} as const satisfies Record<HrTab, PermissionRequirement>

/** Chemin de la route dédiée de chaque onglet — source unique consommée par la barre d'onglets
 * (navigation) et par la redirection de `/hr/planning`. */
export const HR_TAB_ROUTE = {
  dashboard: '/hr/planning/dashboard',
  calendar: '/hr/planning/calendar',
  collaborators: '/hr/planning/collaborators',
  attendance: '/hr/planning/attendance',
  'absence-calendar': '/hr/planning/absence-calendar',
} as const satisfies Record<HrTab, string>

/** Les onglets n'ayant pas de route dédiée, chacun porte explicitement son exigence. */
type HrTabDefinition = GuardedTab<HrTab> & { permission: PermissionRequirement }

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const HR_TABS: HrTabDefinition[] = [
  { value: 'dashboard', label: 'Tableau de bord', permission: HR_TAB_PERMISSIONS.dashboard },
  { value: 'calendar', label: 'Planning', permission: HR_TAB_PERMISSIONS.calendar },
  { value: 'collaborators', label: 'Personnel', permission: HR_TAB_PERMISSIONS.collaborators },
  { value: 'attendance', label: 'Pointage', permission: HR_TAB_PERMISSIONS.attendance },
  {
    value: 'absence-calendar',
    label: 'Calendrier Absences',
    permission: HR_TAB_PERMISSIONS['absence-calendar'],
  },
]
